<template>
  <div class="auth-page">
    <main class="auth-main">
      <section class="auth-copy" aria-labelledby="signin-title">
        <h1 id="signin-title" class="sr-only">
          {{ $t('signin.title') }}
        </h1>
        <div class="auth-art" aria-hidden="true">
          <img src="/images/girl-computer.png" alt="">
        </div>
        <p class="auth-lede">
          {{ pageCopy.intro }}
        </p>
      </section>

      <section class="auth-panel" aria-label="Sign in form">
        <div class="form-section">
          <h2>{{ $t('signin.title') }}</h2>
          
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <form @submit.prevent="handleSubmit">
            <div class="form-group">
              <label for="email">{{ $t('signin.email') }}</label>
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                required
                :placeholder="$t('signin.emailPlaceholder')"
              >
            </div>

            <div class="form-group">
              <label for="password">{{ $t('signin.password') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  id="password" 
                  v-model="password" 
                  required
                  :placeholder="$t('signin.passwordPlaceholder')"
                >
                <span class="password-toggle" @click="togglePasswordVisibility">
                  <i :class="showPassword ? 'eye-icon eye-slash' : 'eye-icon'"></i>
                </span>
              </div>
              <router-link to="/forgot-password" class="forgot-password-link">
                {{ $t('signin.forgotPassword') }}
              </router-link>
            </div>

            <button type="submit" :disabled="isLoading">
              {{ isLoading ? $t('signin.signingIn') : $t('signin.submit') }}
            </button>

            <p class="signup-link">
              {{ $t('signin.noAccount') }} <br>
              <router-link to="/signup">{{ $t('auth.signup') }}</router-link>
            </p>
          </form>
        </div>
      </section>
    </main>

    <SharewellFooter />
  </div>
</template>

<script>
import axios from 'axios';
import SharewellFooter from '../sharewell_footer.vue';
import eventBus from '../eventBus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const PAGE_COPY = {
  en: {
    intro: 'Sign in to find your saved cards and finished PDFs whenever you need them.'
  },
  fr: {
    intro: 'Connectez-vous pour retrouver vos cartes sauvegardées et vos PDF terminés quand vous en avez besoin.'
  },
  es: {
    intro: 'Inicia sesión para encontrar tus tarjetas guardadas y tus PDF terminados cuando los necesites.'
  }
};

export default {
  name: 'AccountSignin',
  components: {
    SharewellFooter
  },
  setup() {
    const router = useRouter();
    const { locale, t } = useI18n();
    const pageCopy = computed(() => PAGE_COPY[locale.value] || PAGE_COPY.en);

    const email = ref('');
    const password = ref('');
    const errorMessage = ref('');
    const isLoading = ref(false);
    const showPassword = ref(false);

    const togglePasswordVisibility = () => {
      showPassword.value = !showPassword.value;
    };

    const handleSubmit = async () => {
      errorMessage.value = '';
      try {
        isLoading.value = true;
        const response = await axios.post('/auth/signin', {
          email: email.value,
          password: password.value
        });
        if (response.data.user) {
          await new Promise((resolve) => {
            eventBus.emit('auth:login', resolve);
          });
          router.push('/my-cards');
        }
      } catch (error) {
        errorMessage.value = (error.response && error.response.data && error.response.data.error) || t('signin.generalError');
      } finally {
        isLoading.value = false;
      }
    };

    return {
      email,
      password,
      errorMessage,
      isLoading,
      pageCopy,
      showPassword,
      togglePasswordVisibility,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.auth-page {
  --ink: #302a27;
  --ink-soft: #6c6159;
  --ink-muted: #92857b;
  --paper: #fff8f0;
  --paper-cream: #fffdf7;
  --paper-line: rgba(87, 65, 52, 0.16);
  --title-red: #c94750;
  --coral: #e75a62;
  --coral-hover: #d64b55;
  --aqua: #d9f3f2;
  display: flow-root;
  min-height: 100vh;
  color: var(--ink);
  background: transparent;
  font-family: "Sharewell Glacial", "Avenir Next", "Helvetica Neue", Arial, sans-serif;
}

.auth-page::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 253, 247, 0.04), rgba(255, 253, 247, 0.72));
}

.auth-page > * {
  position: relative;
  z-index: 1;
}

.auth-main {
  width: min(960px, calc(100% - 48px));
  display: grid;
  grid-template-columns: minmax(270px, 0.78fr) minmax(320px, 1fr);
  gap: clamp(24px, 3.6vw, 46px);
  align-items: center;
  border: 1px solid rgba(87, 65, 52, 0.14);
  border-radius: 12px;
  background: rgba(255, 253, 247, 0.78);
  box-shadow: 0 28px 54px -42px rgba(63, 44, 34, 0.38);
  margin: clamp(42px, 6vw, 78px) auto clamp(56px, 7vw, 96px);
  padding: clamp(28px, 3.6vw, 42px);
}

.auth-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.auth-lede {
  max-width: 30ch;
  margin: clamp(14px, 2vw, 20px) auto 0;
  color: var(--ink-muted);
  font-size: clamp(0.9rem, 1vw, 0.98rem);
  line-height: 1.5;
  text-align: center;
}

.auth-art {
  position: relative;
  width: min(330px, 100%);
  min-height: clamp(230px, 28vw, 330px);
  margin: 0 auto;
}

.auth-art img {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  filter: drop-shadow(0 20px 16px rgba(68, 48, 34, 0.14));
}

.auth-panel {
  min-width: 0;
}

.form-section {
  max-width: 430px;
  margin: 0 auto;
}

h2 {
  margin: 0 0 24px;
  color: var(--title-red);
  font-family: "DM Serif Text", Georgia, serif;
  font-size: clamp(2.1rem, 3vw, 3.1rem);
  font-weight: 400;
  line-height: 1;
  text-align: left;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  color: var(--ink);
  font-weight: 800;
}

input {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--paper-line);
  border-radius: 10px;
  background: #ffffff;
  color: var(--ink);
  font-size: 16px;
  padding: 0 14px;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: var(--coral);
  box-shadow: inset 0 0 0 1px var(--coral);
}

button {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--coral);
  border-radius: 14px;
  background: var(--coral);
  color: var(--paper-cream);
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition:
    background-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 220ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

button:hover {
  border-color: var(--coral-hover);
  background: var(--coral-hover);
  transform: translateY(-2px);
}

button:disabled {
  border-color: rgba(87, 65, 52, 0.16);
  background: rgba(87, 65, 52, 0.18);
  cursor: not-allowed;
  transform: none;
}

.error-message {
  color: var(--title-red);
  margin-bottom: 20px;
  padding: 12px 14px;
  border: 1px solid rgba(201, 71, 80, 0.22);
  border-radius: 10px;
  background: rgba(201, 71, 80, 0.08);
}

.signup-link {
  text-align: center;
  margin-top: 20px;
  color: var(--ink-soft);
  line-height: 1.5;
}

.signup-link a {
  color: var(--title-red);
  font-weight: 800;
  text-decoration: none;
}

.signup-link a:hover {
  text-decoration: underline;
}

.forgot-password-link {
  display: block;
  text-align: right;
  margin-top: 8px;
  font-size: 14px;
  color: var(--title-red);
  font-weight: 800;
  text-decoration: none;
}

.forgot-password-link:hover {
  text-decoration: underline;
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-container input {
  padding-right: 35px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.eye-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'%3E%3Cpath fill='%236c6159' d='M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z'/%3E%3C/svg%3E") no-repeat center center;
  background-size: contain;
}

.eye-slash {
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512'%3E%3Cpath fill='%236c6159' d='M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z'/%3E%3C/svg%3E") no-repeat center center;
  background-size: contain;
}

@media (max-width: 900px) {
  .auth-main {
    grid-template-columns: 1fr;
    min-height: auto;
    align-items: start;
  }

  .auth-lede {
    max-width: 34ch;
  }

  .auth-art {
    min-height: 250px;
  }
}

@media (max-width: 560px) {
  .auth-main {
    width: min(100% - 28px, 720px);
    padding: 26px 20px;
  }

  .auth-lede {
    font-size: 1.12rem;
  }

  .auth-art {
    display: none;
  }

}
</style>
